import * as C from '../../styles';

import { isValueExist, toggleInList } from '@util';
import { refineChannelIconName } from '@/utils/dashboard';
import { getChannelFilterName, getChannelName } from '@/utils/channelName';

import Checkbox from '@components/Common/Checkbox.js';
import CommonButton from '@design/components/button/Button';

import channelIcon from '@/mixins/channelIcons';
import SearchIcon from '@svgIcons/search.svg';
import { mapGetters } from 'vuex';
import { FILTER_ITEM_CLASS } from '@/utils/consts';

import { refineRawText } from '@/utils/translator';

export default {
    name: 'ChannelCheckbox',
    mixins: [channelIcon],
    props: {
        selectedItems: {
            type: Array,
            default() {
                return [];
            },
        },
        options: {
            type: Array,
            default() {
                return [];
            },
        },
        enableBlock: { type: Boolean, default: true },
        normalizeText: {
            type: Function,
        },
        isNewFilterUI: {
            type: Boolean,
            default: false,
        },
        allowEmptyValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedChannelList: this.selectedItems,
            keyword: '',
        };
    },
    computed: {
        ...mapGetters('newDashboard', [
            'tableAdAccountsByAlternativeChannelName',
        ]),

        filteredChannelList() {
            const { keyword, options, normalizeText, channelFormatter } = this;
            const filterText = normalizeText(keyword);

            return keyword
                ? options.filter((option) => {
                      const text = channelFormatter(option);
                      return normalizeText(text).indexOf(filterText) > -1;
                  })
                : options;
        },
        sortedChannels() {
            const { channelFormatter } = this;

            return R.compose(
                R.sortWith([R.ascend(R.prop('text'))]),
                R.map((channel) => {
                    return {
                        channel,
                        text: channelFormatter(channel),
                    };
                }),
                R.propOr([], 'filteredChannelList')
            )(this);
        },
    },
    methods: {
        channelFormatter(channel) {
            const adAccount =
                this.tableAdAccountsByAlternativeChannelName[channel];
            const displayName = adAccount
                ? this.userLang === 'ko'
                    ? adAccount.displayChannelNameKR ||
                      adAccount.displayChannelName
                    : adAccount.displayChannelName
                : '';

            return getChannelFilterName.call(
                this,
                channel,
                displayName ||
                    getChannelName.call(this, channel, refineRawText(channel))
            );
        },
        updateSelectedItem(id) {
            const { selectedChannelList: list } = this;

            this.selectedChannelList = toggleInList({
                pred: R.equals(id),
                list,
                value: id,
            });
        },
        getDataList() {
            const {
                selectedChannelList,
                updateSelectedItem,
                sortedChannels,
                selectedItems,
                enableBlock,
                keyword,
                isNewFilterUI,
            } = this;

            const hasData = sortedChannels.length > 0;
            const blockedItemTooltip = this.$t(
                'FILTERS.blocked_item_tooltip_msg'
            );

            return hasData ? (
                <C.ListWrapper>
                    {sortedChannels.map(({ channel, text }) => {
                        const foundChannel = selectedChannelList.find(
                            R.equals(channel)
                        );
                        const blockedItem = selectedItems.find(
                            R.equals(channel)
                        );

                        const isBlocked =
                            enableBlock && isValueExist(blockedItem);

                        return (
                            <C.ItemWrapper
                                class="item"
                                onClick={() => {
                                    if (isBlocked) return;
                                    updateSelectedItem(channel);
                                }}
                            >
                                <Checkbox
                                    value={Boolean(foundChannel) || isBlocked}
                                    isBlock={isBlocked}
                                    tooltip={
                                        isBlocked
                                            ? blockedItemTooltip
                                            : undefined
                                    }
                                />
                                <C.Logo>
                                    {this.channelIconWithAdverity(
                                        refineChannelIconName(channel)
                                    )}
                                </C.Logo>
                                <C.ItemTitle>{text}</C.ItemTitle>
                            </C.ItemWrapper>
                        );
                    })}
                </C.ListWrapper>
            ) : isNewFilterUI ? (
                <li
                    class={FILTER_ITEM_CLASS}
                    onClick={() => {
                        this.$emit(
                            'submit',
                            R.uniq([...selectedChannelList, keyword])
                        );
                    }}
                >
                    <p
                        domPropsInnerHTML={this.$t(
                            `FILTERS.generic_apply_keyword_value`,
                            {
                                keywords: keyword,
                            }
                        )}
                    ></p>
                </li>
            ) : (
                <div
                    class="no-data"
                    domPropsInnerHTML={this.$t(
                        'NEW_DASHBOARD.filter_search_no_data'
                    )}
                ></div>
            );
        },
    },
    mounted() {
        const inputBox = this.$refs.keywordInputbox;
        inputBox && inputBox.focus();
    },
    render() {
        const { selectedChannelList, filteredChannelList, isNewFilterUI } =
            this;

        const hasData = filteredChannelList.length > 0;
        const hasSelectedChannelList = selectedChannelList.length > 0;

        return (
            <C.Wrapper>
                <C.SearchHeader>
                    <input
                        onInput={(e) => {
                            this.keyword = e.target.value;
                        }}
                        placeholder={this.$t('NEW_DASHBOARD.filter_search')}
                        class={'search-bar'}
                        ref={'keywordInputbox'}
                    />
                    <div class="icon-wrapper">
                        <SearchIcon v-view-box width="16px" height="16px" />
                    </div>
                </C.SearchHeader>

                <C.ResultWrapper
                    hasData={hasData}
                    isNewFilterUI={isNewFilterUI}
                >
                    {this.getDataList()}
                </C.ResultWrapper>

                <C.ButtonWrapper>
                    {hasSelectedChannelList && (
                        <CommonButton
                            class="unselect-btn"
                            type="secondary"
                            onClick={() => {
                                this.selectedChannelList = [];
                            }}
                        >
                            {this.$t('COMMON.unselect_all')}
                        </CommonButton>
                    )}

                    <CommonButton
                        disabled={
                            !hasSelectedChannelList && !this.allowEmptyValue
                        }
                        onClick={() => {
                            if (hasSelectedChannelList || this.allowEmptyValue) {
                                const channelList = this.enableBlock
                                    ? selectedChannelList.filter(
                                          R.compose(
                                              R.complement,
                                              R.includes(
                                                  R.__,
                                                  this.selectedItems
                                              )
                                          )
                                      )
                                    : selectedChannelList;

                                this.$emit('submit', channelList);
                            }
                        }}
                    >
                        {this.$t('COMMON.apply')}
                    </CommonButton>
                </C.ButtonWrapper>
            </C.Wrapper>
        );
    },
};
